import { useState, useEffect, useMemo } from 'react';
import { useSearchParams } from 'react-router-dom';
import {
  Box,
  Stack,
  Typography,
  TextField,
  Button,
  Card,
  CardContent,
  Accordion,
  AccordionSummary,
  AccordionDetails,
  Chip,
  IconButton,
  Dialog,
  DialogTitle,
  DialogContent,
  DialogActions,
  Divider,
  Alert,
  CircularProgress,
  Pagination,
  FormControlLabel,
  Switch,
  Autocomplete,
  Checkbox,
  InputAdornment
} from '@mui/material';
import SearchIcon from '@mui/icons-material/Search';
import ExpandMoreIcon from '@mui/icons-material/ExpandMore';
import LocalShippingIcon from '@mui/icons-material/LocalShipping';
import InventoryIcon from '@mui/icons-material/Inventory';
import DescriptionIcon from '@mui/icons-material/Description';
import CheckCircleIcon from '@mui/icons-material/CheckCircle';
import DeleteIcon from '@mui/icons-material/Delete';
import ErrorOutlineIcon from '@mui/icons-material/ErrorOutline';
import InfoOutlinedIcon from '@mui/icons-material/InfoOutlined';
import AddIcon from '@mui/icons-material/Add';
import Tooltip from '@mui/material/Tooltip';
import useObserveTripSheets from '../../../hooks/useSubscribeTripsheet';
import { useCurrentUser, useDatastore } from '../../../hooks';
import { useAlert } from '../../../hooks';
import dayjs from 'dayjs';
import {
  addSSCCToManifest,
  deleteManifest,
  deleteSSCC,
  markManifestCompleted
} from '../../../services/adminManifestActions';
import { ReceiptEventPackageProps, SSCCProps } from '../../../datastore/ssccs';
import useObserveSsccs from '../../../hooks/useSubscribeSsccs';
import { dateFormat, timeFormat } from '../../../common/dateTimes';
import { SsccStatus } from '../../../common';
import {
  getTripDisplayStatus,
  ManifestStatus,
  mapSsccStatusToAsnStatus,
  mapTripLegStatusToSsccStatus,
  TripLegStatus
} from '../../../common/status';
import { ManifestLegProps, ManifestProps, ManifestSSCCProps, TripSheetProps } from '../../../datastore/tripSheets';

export interface Manifest {
  id: string;
  tripId: string;
  tripReference: string;
  tripSheetId: string;
  trailerId: string;
  ssccs: ManifestSSCCProps[];
  legId: string;
  status: ManifestStatus;
  ssccsWithAsns: ManifestSSCC[];
  createdAt: string;
  destination: string;
  isDeleted: boolean;
}

interface ManifestSSCC {
  ssccId: string;
  sscc: SSCCProps | undefined;
  asns: ReceiptEventPackageProps[];
}

export interface SelectedManifest {
  manifest: Manifest;
  tripDisplayStatus: TripLegStatus;
  ssccStatus: string;
  asnStatus: string;
  tripId: string;
  legId: string;
  ssccId?: string;
}

const TRIP_FILTER_URL = 'trip';
const MANIFEST_FILTER_URL = 'manifest';
const ASN_FILTER_URL = 'asn';
const SSCC_FILTER_URL = 'sscc';
const DATE_FILTER_URL = 'date';

export default function ManifestManagement() {
  const user = useCurrentUser();
  const alert = useAlert();
  const rawTripSheets = useObserveTripSheets();
  const [searchParams, setSearchParams] = useSearchParams();
  const [tripSearch, setTripSearch] = useState(searchParams.get(TRIP_FILTER_URL) || '');
  const [manifestSearch, setManifestSearch] = useState(searchParams.get(MANIFEST_FILTER_URL) || '');
  const [asnSearch, setAsnSearch] = useState(searchParams.get(ASN_FILTER_URL) || '');
  const [ssccSearch, setSsccSearch] = useState(searchParams.get(SSCC_FILTER_URL) || '');
  const [selectedDate, setSelectedDate] = useState(searchParams.get(DATE_FILTER_URL) || '');
  const [currentPage, setCurrentPage] = useState(1);
  const [selectedManifest, setSelectedManifest] = useState<SelectedManifest | null>(null);
  const [openCompleteDialog, setOpenCompleteDialog] = useState(false);
  const [openDeleteDialog, setOpenDeleteDialog] = useState(false);
  const [openDeleteSSCCDialog, setOpenDeleteSSCCDialog] = useState(false);
  const [actionReason, setActionReason] = useState('');
  const [hideEmptyTrips, setHideEmptyTrips] = useState(true);
  const [isProcessing, setIsProcessing] = useState(false);
  const [openAddSSCCDialog, setOpenAddSSCCDialog] = useState(false);
  const [selectedSSCCId, setSelectedSSCCId] = useState<string>('');
  const [addSSCCReason, setAddSSCCReason] = useState('');
  const [ssccWeight, setSsccWeight] = useState<string>('0');
  const [withReceiveManifest, setWithReceiveManifest] = useState(true);
  const [availableSSCCs, setAvailableSSCCs] = useState<any[]>([]);
  const [openBustleDialog, setOpenBustleDialog] = useState(false);
  const [openCoupaDialog, setOpenCoupaDialog] = useState(false);
  const [showOnlyDuplicates, setShowOnlyDuplicates] = useState(false);
  const store = useDatastore();
  const ssccs = useObserveSsccs(0);

  const selectedSSCC = useMemo(() => {
    return ssccs?.find(({ id }) => id === selectedSSCCId);
  }, [selectedSSCCId, ssccs]);

  const tripSheets = useMemo(() => {
    return [...rawTripSheets].sort((a, b) => String(b.reference).localeCompare(String(a.reference)));
  }, [rawTripSheets]);

  // Update URL when manifest search changes
  useEffect(() => {
    const params = new URLSearchParams(searchParams);
    if (manifestSearch) {
      params.set(MANIFEST_FILTER_URL, manifestSearch);
    } else {
      params.delete(MANIFEST_FILTER_URL);
    }
    if (tripSearch) {
      params.set(TRIP_FILTER_URL, tripSearch);
    } else {
      params.delete(TRIP_FILTER_URL);
    }
    if (asnSearch) {
      params.set(ASN_FILTER_URL, asnSearch);
    } else {
      params.delete(ASN_FILTER_URL);
    }
    if (ssccSearch) {
      params.set(SSCC_FILTER_URL, ssccSearch);
    } else {
      params.delete(SSCC_FILTER_URL);
    }
    if (selectedDate) {
      params.set(DATE_FILTER_URL, selectedDate);
    } else {
      params.delete(DATE_FILTER_URL);
    }
    setSearchParams(params, { replace: true });
  }, [manifestSearch, searchParams, tripSearch, asnSearch, ssccSearch, selectedDate, setSearchParams]);

  // Detect duplicate manifests across all trips (must be before filters that use it)
  const manifestOccurrences = useMemo(() => {
    return tripSheets.reduce<Map<string, number>>((map, trip) => {
      trip.legs.forEach((leg) => {
        leg.manifests.forEach((manifest) => {
          map.set(manifest.id, (map.get(manifest.id) ?? 0) + 1);
        });
      });
      return map;
    }, new Map());
  }, [tripSheets]);

  // Filter tripsheets based on search query and date
  const filteredTripSheets = useMemo(() => {
    return (
      tripSheets
        // Filter by trip reference search query
        .filter((trip) => (tripSearch.trim() ? trip.reference?.toLowerCase().includes(tripSearch.toLowerCase()) : true))

        // Filter by manifest number
        .filter((trip) =>
          manifestSearch.trim()
            ? trip.legs.some((leg) =>
                leg.manifests.some((manifest) => manifest.id.toLowerCase().includes(manifestSearch.toLowerCase()))
              )
            : true
        )

        // Filter by ASN
        .filter((trip) =>
          asnSearch.trim()
            ? trip.legs.some((leg) =>
                leg.manifests.some((manifest) =>
                  manifest.ssccs.some((sscc: any) =>
                    sscc.packages.some((pkg: any) => pkg.asn?.toLowerCase().includes(asnSearch.toLowerCase()))
                  )
                )
              )
            : true
        )

        // Filter by SSCC
        .filter((trip) =>
          ssccSearch.trim()
            ? trip.legs.some((leg) =>
                leg.manifests.some((manifest) =>
                  manifest.ssccs.some((sscc: any) => sscc.ssccId?.toLowerCase().includes(ssccSearch.toLowerCase()))
                )
              )
            : true
        )

        // Filter by date
        .filter((trip) =>
          selectedDate ? trip.createdAt && dayjs(trip.createdAt).format('YYYY-MM-DD') === selectedDate : true
        )

        // Filter out trips with no manifests if toggle is active
        .filter((trip) => (hideEmptyTrips ? trip.legs.reduce((sum, leg) => sum + leg.manifests.length, 0) > 0 : true))

        // Filter to show only trips with duplicate manifests if toggle is active
        .filter((trip) =>
          showOnlyDuplicates
            ? trip.legs.some((leg) => leg.manifests.some((manifest) => (manifestOccurrences.get(manifest.id) ?? 0) > 1))
            : true
        )
    );
  }, [
    tripSheets,
    tripSearch,
    manifestSearch,
    asnSearch,
    ssccSearch,
    selectedDate,
    hideEmptyTrips,
    showOnlyDuplicates,
    manifestOccurrences
  ]);

  // Pagination logic
  const pageSize = 25;

  const totalPages = useMemo(() => Math.ceil(filteredTripSheets.length / pageSize), [filteredTripSheets.length]);

  const paginatedTripSheets = useMemo(() => {
    const startIndex = (currentPage - 1) * pageSize;
    return filteredTripSheets.slice(startIndex, startIndex + pageSize);
  }, [filteredTripSheets, currentPage]);

  // Reset to page 1 when filters change
  const handleTripSearchChange = (value: string) => {
    setTripSearch(value);
    setCurrentPage(1);
  };

  const handleManifestSearchChange = (value: string) => {
    setManifestSearch(value);
    setCurrentPage(1);
  };

  const handleAsnSearchChange = (value: string) => {
    setAsnSearch(value);
    setCurrentPage(1);
  };

  const handleSsccSearchChange = (value: string) => {
    setSsccSearch(value);
    setCurrentPage(1);
  };

  const handleDateChange = (value: string) => {
    setSelectedDate(value);
    setCurrentPage(1);
  };

  const handlePageChange = (event: React.ChangeEvent<unknown>, value: number) => {
    setCurrentPage(value);
    window.scrollTo({ top: 0, behavior: 'smooth' });
  };

  // Transform tripsheet data to include manifests with extracted SSCCs and ASNs
  const transformManifest = (manifest: ManifestProps, trip: TripSheetProps, leg: ManifestLegProps): Manifest => {
    const ssccsWithAsns: ManifestSSCC[] = manifest.ssccs.map((localSSCC): ManifestSSCC => {
      const sscc = ssccs.find((s) => s.id === localSSCC.ssccId);

      return {
        ssccId: localSSCC.ssccId,
        sscc,
        asns: sscc?.receiptedEvents.filter(({ packages }) => packages.length).flatMap(({ packages }) => packages) || []
      };
    });

    return {
      id: manifest.id,
      tripId: manifest.tripId,
      tripSheetId: trip.id,
      legId: leg.id,
      tripReference: trip.reference || '',
      trailerId: manifest.trailerId,
      ssccs: manifest.ssccs,
      status: manifest.status.toString() as ManifestStatus,
      ssccsWithAsns,
      createdAt: trip.createdAt ? dayjs(trip.createdAt).format('YYYY-MM-DD') : '',
      destination: leg.destination.businessName,
      isDeleted: false
    };
  };

  const handleMarkAsCompleted = async () => {
    if (!selectedManifest || !actionReason.trim() || isProcessing) {
      alert('Unexpected error', 'error');
      return;
    }

    try {
      setIsProcessing(true);

      const result = await markManifestCompleted({
        selectedManifest,
        actionReason,
        alert,
        store,
        user,
        tripData: tripSheets.find((t) => t.id === selectedManifest.tripId),
        onlySendToCoupa: false
      });

      if (result) {
        setOpenCompleteDialog(false);
        setSelectedManifest(null);
        setActionReason('');
      }
    } catch (error) {
      console.error('Failed to mark manifest as completed:', error);
      alert('An unexpected error occurred', 'error');
    } finally {
      setIsProcessing(false);
    }
  };

  const handleDeleteManifest = async () => {
    if (!selectedManifest || !actionReason.trim() || isProcessing) {
      alert('Unexpected error', 'error');
      return;
    }

    try {
      setIsProcessing(true);

      const result = await deleteManifest({
        selectedManifest,
        actionReason,
        alert,
        store,
        user
      });

      if (result) {
        alert('Manifest has been removed.', 'success');
        setOpenDeleteDialog(false);
        setSelectedManifest(null);
        setActionReason('');

        selectedManifest.tripDisplayStatus === TripLegStatus.completed && setOpenCoupaDialog(true);
      }
    } catch (error) {
      console.error('Failed to delete manifest:', error);
      alert('An unexpected error occurred', 'error');
    } finally {
      setIsProcessing(false);
    }
  };

  const handleDeleteSSCC = async () => {
    if (!selectedManifest || !actionReason.trim() || isProcessing || !selectedManifest.ssccId) {
      alert('Unexpected error', 'error');
      return;
    }

    try {
      setIsProcessing(true);

      const result = await deleteSSCC({
        selectedManifest,
        actionReason,
        alert,
        store,
        user
      });

      if (result) {
        alert('SSCC has been removed.', 'success');
        setOpenDeleteSSCCDialog(false);
        setSelectedManifest(null);
        setActionReason('');

        selectedManifest.tripDisplayStatus === TripLegStatus.completed && setOpenCoupaDialog(true);
      }
    } catch (error) {
      console.error('Failed to delete SSCC:', error);
      alert('An unexpected error occurred', 'error');
    } finally {
      setIsProcessing(false);
    }
  };

  const handleOpenAddSSCCDialog = async ({
    manifest,
    tripDisplayStatus,
    ssccStatus,
    asnStatus,
    tripId,
    legId
  }: {
    manifest: Manifest;
    tripDisplayStatus: TripLegStatus;
    ssccStatus: string;
    asnStatus: string;
    tripId: string;
    legId: string;
    ssccId?: string;
  }) => {
    setSelectedManifest({ manifest, tripDisplayStatus, ssccStatus, asnStatus, tripId, legId });
    setOpenAddSSCCDialog(true);
    // Load available SSCCs that are not already in this manifest
    try {
      const allSSCCsOpen = await store.ssccs.getList('', undefined, 'open', true);
      const allSSCCsStaged = await store.ssccs.getList('', undefined, 'staged', true);
      const allSSCCs = [...(allSSCCsOpen || []), ...(allSSCCsStaged || [])].sort((a, b) =>
        String(b.id).localeCompare(String(a.id))
      );
      if (allSSCCs) {
        // Filter out SSCCs already in the manifest
        const filteredSSCCs = allSSCCs.filter(
          (sscc) => !manifest.ssccsWithAsns.map(({ ssccId }) => ssccId).includes(sscc.id)
        );

        // Fetch destination locations for each SSCC
        const ssccsWithLocations = await Promise.all(
          filteredSSCCs.map(async (s) => {
            const destinationLocation = s.destinationLocationId
              ? await store.locations.getById(s.destinationLocationId)
              : undefined;
            return {
              ...s,
              destinationLocation
            };
          })
        );

        setAvailableSSCCs(ssccsWithLocations);
      }
    } catch (error) {
      console.error('Error loading SSCCs:', error);
    }
  };

  const handleAddSSCCToManifest = async ({ withReceiveManifestCheck }: { withReceiveManifestCheck: boolean }) => {
    if (!selectedManifest || !selectedSSCCId || !addSSCCReason.trim() || isProcessing) {
      alert('Unexpected error', 'error');
      return;
    }

    const receiveManifest = withReceiveManifestCheck && withReceiveManifest;

    try {
      setIsProcessing(true);

      const result = await addSSCCToManifest({
        selectedManifest,
        selectedSSCCId,
        receiveManifest,
        selectedSSCCWeight: selectedSSCC?.weight,
        ssccWeight,
        tripSheets,
        addSSCCReason,
        alert,
        store,
        user
      });

      if (result) {
        alert('SSCC successfully added to manifest', 'success');
        setOpenAddSSCCDialog(false);
        setSelectedSSCCId('');
        setAddSSCCReason('');
        setSsccWeight('0');
        setWithReceiveManifest(true);
        setSelectedManifest(null);

        // Show Bustle dialog
        setOpenBustleDialog(true);
      }
    } catch (error) {
      console.error('Failed to add SSCC to manifest:', error);
      alert('An unexpected error occurred', 'error');
    } finally {
      setIsProcessing(false);
    }
  };

  const getTimeBasedOnStatus = ({ status, sscc }: { status: SsccStatus; sscc: SSCCProps | undefined }) => {
    if (!sscc) {
      return '';
    }

    let isoDate;

    switch (status) {
      case SsccStatus.open:
        isoDate = sscc?.open;
        break;

      case SsccStatus.staged:
        isoDate = sscc?.staged;
        break;

      case SsccStatus.manifested:
        isoDate = sscc?.manifested;
        break;

      case SsccStatus.inTransit:
        isoDate = sscc?.inTransit;
        break;

      case SsccStatus.approaching:
        isoDate = sscc?.approaching;
        break;

      case SsccStatus.delivered:
        isoDate = sscc?.delivered;
        break;

      default:
        return '';
    }

    if (!isoDate) return '';

    const date = isoDate?.format(dateFormat);
    const time = isoDate?.format(timeFormat);

    return `${date} ${time}`;
  };

  return (
    <Stack spacing={3} sx={{ px: 3 }}>
      {/* Search Section */}
      <Card
        sx={{
          boxShadow: '0 2px 12px rgba(0,0,0,0.06)',
          border: '1px solid rgba(0,0,0,0.06)',
          borderRadius: 2
        }}>
        <CardContent sx={{ p: 3 }}>
          <Stack spacing={2.5}>
            <Stack direction="row" alignItems="center" justifyContent="space-between">
              <Stack width={'100%'} direction={'row'} justifyContent={'space-between'} alignItems={'center'}>
                <Stack direction="row" spacing={1.5} alignItems="center">
                  <Box
                    sx={{
                      p: 1,
                      borderRadius: 1.5,
                      backgroundColor: 'rgba(25, 118, 210, 0.08)',
                      display: 'flex',
                      alignItems: 'center',
                      justifyContent: 'center'
                    }}>
                    <SearchIcon sx={{ color: '#1976d2', fontSize: 20 }} />
                  </Box>
                  <Typography variant="h6" fontWeight={600} sx={{ color: 'text.primary' }}>
                    Filters
                  </Typography>

                  {(tripSearch || manifestSearch || asnSearch || ssccSearch || selectedDate) && (
                    <Button
                      variant="outlined"
                      size="small"
                      onClick={() => {
                        setTripSearch('');
                        setManifestSearch('');
                        setAsnSearch('');
                        setSsccSearch('');
                        setSelectedDate('');
                        setCurrentPage(1);
                      }}
                      sx={{
                        whiteSpace: 'nowrap',
                        alignSelf: 'flex-start',
                        fontSize: '0.85rem',

                        my: 2,
                        ml: 2
                      }}>
                      Clear All Filters
                    </Button>
                  )}
                </Stack>

                <Stack direction="row" spacing={2} alignItems="center">
                  <FormControlLabel
                    control={
                      <Switch
                        checked={hideEmptyTrips}
                        onChange={(e) => setHideEmptyTrips(e.target.checked)}
                        size="small"
                      />
                    }
                    label={
                      <Typography variant="subtitle2" fontWeight={500} sx={{ color: 'text.secondary' }}>
                        Hide Empty Trips
                      </Typography>
                    }
                  />
                  <FormControlLabel
                    control={
                      <Switch
                        checked={showOnlyDuplicates}
                        onChange={(e) => {
                          setShowOnlyDuplicates(e.target.checked);
                        }}
                        size="small"
                      />
                    }
                    label={
                      <Typography variant="subtitle2" fontWeight={500} sx={{ color: 'text.secondary' }}>
                        Show Only Duplicates
                      </Typography>
                    }
                  />
                </Stack>
              </Stack>
            </Stack>
            <Stack spacing={2}>
              <Stack direction="row" spacing={2} alignItems="center">
                <TextField
                  fullWidth
                  placeholder="Search by Trip Number..."
                  value={tripSearch}
                  onChange={(e) => handleTripSearchChange(e.target.value)}
                  size="small"
                  InputProps={{
                    startAdornment: <LocalShippingIcon sx={{ color: 'text.secondary', mr: 1, fontSize: 20 }} />
                  }}
                  sx={{
                    '& .MuiOutlinedInput-root': {
                      backgroundColor: tripSearch ? 'rgba(25, 118, 210, 0.08)' : 'background.default',
                      fontSize: '0.85rem'
                    }
                  }}
                />

                <TextField
                  fullWidth
                  placeholder="Search by Manifest Number..."
                  value={manifestSearch}
                  onChange={(e) => handleManifestSearchChange(e.target.value)}
                  size="small"
                  InputProps={{
                    startAdornment: <InventoryIcon sx={{ color: 'text.secondary', mr: 1, fontSize: 20 }} />
                  }}
                  sx={{
                    '& .MuiOutlinedInput-root': {
                      backgroundColor: manifestSearch ? 'rgba(25, 118, 210, 0.08)' : 'background.default',
                      fontSize: '0.85rem'
                    }
                  }}
                />
                <TextField
                  fullWidth
                  placeholder="Search by SSCC..."
                  value={ssccSearch}
                  onChange={(e) => handleSsccSearchChange(e.target.value)}
                  size="small"
                  InputProps={{
                    startAdornment: <InventoryIcon sx={{ color: 'text.secondary', mr: 1, fontSize: 20 }} />
                  }}
                  sx={{
                    '& .MuiOutlinedInput-root': {
                      backgroundColor: ssccSearch ? 'rgba(25, 118, 210, 0.08)' : 'background.default',
                      fontSize: '0.85rem'
                    }
                  }}
                />
                <TextField
                  fullWidth
                  placeholder="Search by ASN..."
                  value={asnSearch}
                  onChange={(e) => handleAsnSearchChange(e.target.value)}
                  size="small"
                  InputProps={{
                    startAdornment: <DescriptionIcon sx={{ color: 'text.secondary', mr: 1, fontSize: 20 }} />
                  }}
                  sx={{
                    '& .MuiOutlinedInput-root': {
                      backgroundColor: asnSearch ? 'rgba(25, 118, 210, 0.08)' : 'background.default',
                      fontSize: '0.85rem'
                    }
                  }}
                />
                <TextField
                  type="date"
                  label="Filter by Date"
                  value={selectedDate}
                  onChange={(e) => handleDateChange(e.target.value)}
                  size="small"
                  InputLabelProps={{
                    shrink: true
                  }}
                  sx={{
                    minWidth: 200,
                    '& .MuiOutlinedInput-root': {
                      backgroundColor: selectedDate ? 'rgba(25, 118, 210, 0.08)' : 'background.default',
                      fontSize: '0.85rem'
                    }
                  }}
                />
              </Stack>
            </Stack>
          </Stack>
        </CardContent>
      </Card>

      {/* TripSheets Section */}
      {filteredTripSheets.length > 0 && (
        <Stack spacing={2}>
          <Stack direction="row" justifyContent="space-between" alignItems="center">
            <Stack direction="row" spacing={1} alignItems="center">
              <Typography variant="h6" fontWeight={600}>
                Trip Sheets ({filteredTripSheets.length})
              </Typography>
              <Tooltip
                title={
                  <Box sx={{ p: 1 }}>
                    <Typography variant="body2" fontWeight={600} sx={{ mb: 0.5 }}>
                      Management Tools
                    </Typography>
                    <Typography variant="caption" component="div" sx={{ mb: 0.5 }}>
                      • Mark as Completed: Manually complete manifests when system/process issues prevent normal
                      completion
                    </Typography>
                    <Typography variant="caption" component="div" sx={{ mb: 0.5 }}>
                      • Delete Manifest: Permanently remove duplicate or erroneous manifests
                    </Typography>
                    <Typography
                      variant="caption"
                      component="div"
                      sx={{ mt: 1, fontStyle: 'italic', color: 'rgba(255,255,255,0.7)' }}>
                      All actions are logged in the audit trail and require a reason
                    </Typography>
                  </Box>
                }
                arrow
                placement="right">
                <IconButton size="small" sx={{ color: 'text.secondary' }}>
                  <InfoOutlinedIcon fontSize="small" />
                </IconButton>
              </Tooltip>
            </Stack>
          </Stack>
          {paginatedTripSheets.map((trip) => (
            <Accordion
              key={trip.id}
              sx={{
                boxShadow: '0 2px 8px rgba(0,0,0,0.08)',
                border: '1px solid rgba(0,0,0,0.06)',
                borderRadius: '8px !important',
                '&:before': { display: 'none' },
                mb: 2,
                position: 'relative',
                zIndex: 0
              }}>
              <AccordionSummary
                expandIcon={<ExpandMoreIcon />}
                sx={{
                  '&:hover': {
                    backgroundColor: 'rgba(0,0,0,0.02)'
                  }
                }}>
                <Stack
                  direction="row"
                  spacing={2}
                  alignItems="stretch"
                  sx={{
                    width: '100%',
                    pr: 2,
                    py: 1.5
                  }}>
                  <Box
                    sx={{
                      p: 1.5,
                      borderRadius: 1.5,
                      backgroundColor: 'rgba(25, 118, 210, 0.08)',
                      display: 'flex',
                      alignItems: 'center',
                      justifyContent: 'center'
                    }}>
                    <LocalShippingIcon sx={{ color: '#1976d2', fontSize: 24 }} />
                  </Box>
                  <Box sx={{ flex: 1 }}>
                    <Stack spacing={0.25}>
                      <Stack direction="row" spacing={1} alignItems="center">
                        <Typography
                          variant="subtitle2"
                          sx={{
                            fontWeight: 600,
                            letterSpacing: '0.2px'
                          }}>
                          Trip #{trip.reference || trip.id}
                        </Typography>
                      </Stack>

                      <Typography
                        variant="subtitle2"
                        sx={{
                          fontWeight: 600,
                          letterSpacing: '0.2px'
                        }}>
                        {trip.carrier?.businessName || 'Unknown Carrier'} •{' '}
                        {trip.createdAt ? dayjs(trip.createdAt).format('DD MMM YYYY') : 'N/A'}
                      </Typography>
                    </Stack>
                  </Box>
                  <Box
                    sx={{
                      minWidth: 480,
                      maxWidth: 560,
                      pl: 3,
                      pr: 1,
                      borderLeft: '1px solid rgba(0,0,0,0.08)',
                      display: 'flex',
                      flexDirection: 'column',
                      justifyContent: 'center'
                    }}>
                    <Stack spacing={1}>
                      {trip.legs.map((leg, legIndex) => (
                        <Box
                          key={leg.id || legIndex}
                          sx={{
                            width: '100%'
                          }}>
                          <Box
                            sx={{
                              px: 3,
                              py: 1.5,
                              my: 1.5,
                              backgroundColor: 'rgba(25, 118, 210, 0.04)',
                              borderRadius: 2,
                              border: '1px solid rgba(25, 118, 210, 0.12)'
                            }}>
                            <Typography variant="subtitle2" sx={{ color: 'text.primary', fontSize: '0.9rem' }}>
                              #{legIndex + 1} {leg.origin?.businessName || 'Unknown'} →{' '}
                              {leg.destination?.businessName || 'Unknown'}
                            </Typography>
                          </Box>

                          <Stack direction="row" spacing={0.75} flexWrap="wrap" sx={{ gap: 0.75 }}>
                            {leg.manifests.length === 0 ? (
                              <Typography
                                variant="body2"
                                sx={{ fontSize: '0.85rem', pl: 1 }}
                                color="text.secondary"
                                fontStyle="italic">
                                No manifests
                              </Typography>
                            ) : (
                              leg.manifests.map((manifest, manifestIndex) => {
                                const isDuplicate = (manifestOccurrences.get(manifest.id) ?? 0) > 1;

                                return (
                                  <Box
                                    key={`${legIndex}-${manifest.id}-${manifestIndex}`}
                                    sx={{
                                      px: 2,
                                      py: 1.25,
                                      minWidth: 120,
                                      borderRadius: 2,
                                      border: isDuplicate ? '2px solid #ff9800' : '1px solid rgba(0,0,0,0.16)',
                                      backgroundColor: 'background.default',
                                      cursor: 'pointer',
                                      boxShadow: '0 1px 3px rgba(0,0,0,0.08)',
                                      transition: 'all 0.15s',
                                      display: 'flex',
                                      flexDirection: 'column',
                                      alignItems: 'center',
                                      justifyContent: 'center',
                                      textAlign: 'center'
                                    }}>
                                    <Typography
                                      variant="body2"
                                      sx={{
                                        fontWeight: 600,
                                        fontSize: '0.85rem',
                                        lineHeight: 1.2
                                      }}>
                                      {manifest.id}
                                    </Typography>
                                    <Typography
                                      variant="caption"
                                      sx={{
                                        display: 'block',
                                        mt: 0.25,
                                        color: 'text.secondary',
                                        fontSize: '0.75rem',
                                        fontWeight: 500
                                      }}>
                                      {getTripDisplayStatus({
                                        manifestStatus: manifest.status,
                                        legStatus: leg.status
                                      })}
                                    </Typography>
                                  </Box>
                                );
                              })
                            )}
                          </Stack>
                        </Box>
                      ))}
                    </Stack>
                  </Box>
                </Stack>
              </AccordionSummary>
              <AccordionDetails
                sx={{
                  px: 4,
                  py: 3
                }}>
                {trip.legs.reduce((sum, leg) => sum + leg.manifests.length, 0) === 0 ? (
                  <Box
                    sx={{
                      textAlign: 'center',
                      py: 5,
                      px: 3,
                      borderRadius: 2,
                      border: '1px dashed rgba(0,0,0,0.12)'
                    }}>
                    <InventoryIcon sx={{ fontSize: 48, color: 'rgba(0,0,0,0.26)', mb: 2 }} />
                    <Typography variant="subtitle1" fontWeight={600} sx={{ mb: 0.5, color: 'text.primary' }}>
                      No Manifests
                    </Typography>
                    <Typography variant="body2" color="text.secondary">
                      This trip doesn't have any manifests associated with it
                    </Typography>
                  </Box>
                ) : (
                  <Box>
                    {trip.legs.map((leg, legIndex) => (
                      <Box key={legIndex}>
                        {legIndex > 0 && (
                          <Divider
                            sx={{
                              my: 4,
                              borderColor: 'rgba(0,0,0,0.08)'
                            }}
                          />
                        )}
                        <Box
                          key={leg.id || legIndex}
                          sx={{
                            mb: 3,
                            boxShadow: '0 2px 8px rgba(0,0,0,0.08)',
                            border: '1px solid rgba(0,0,0,0.06)',
                            borderRadius: '8px !important',
                            backgroundColor: 'rgba(25, 118, 210, 0.02)'
                          }}>
                          <Box
                            sx={{
                              mb: 2,
                              px: 3,
                              py: 2,
                              backgroundColor: 'rgba(25, 118, 210, 0.02)',
                              borderRadius: 2,
                              border: '1px solid rgba(0,0,0,0.08)',
                              boxShadow: '0 1px 3px rgba(0,0,0,0.06)'
                            }}>
                            <Stack direction="row" alignItems="center">
                              <Typography
                                variant="body2"
                                sx={{
                                  fontWeight: 600,
                                  color: 'text.primary'
                                }}>
                                Leg {legIndex + 1}
                              </Typography>
                            </Stack>
                            <Typography
                              variant="caption"
                              sx={{
                                color: 'text.secondary',
                                fontSize: '0.8rem'
                              }}>
                              {leg.origin?.businessName || 'Unknown'} → {leg.destination?.businessName || 'Unknown'}
                            </Typography>
                          </Box>

                          {leg.manifests.length === 0 ? (
                            <Typography
                              variant="body2"
                              color="text.secondary"
                              fontStyle="italic"
                              sx={{ px: 2, fontSize: '0.85rem', pl: 2, mb: 2 }}>
                              No manifests for this leg
                            </Typography>
                          ) : (
                            leg.manifests.map((manifest, manifestIndex) => {
                              const transformedManifest = transformManifest(manifest, trip, leg);
                              const isDuplicate = (manifestOccurrences.get(manifest.id) ?? 0) > 1;
                              const duplicateCount = manifestOccurrences.get(manifest.id) ?? 0;

                              const tripDisplayStatus = getTripDisplayStatus({
                                manifestStatus: manifest.status,
                                legStatus: leg.status
                              });

                              return (
                                <Box sx={{ px: 2 }}>
                                  <Box
                                    key={`${legIndex}-${manifest.id}-${manifestIndex}`}
                                    sx={{
                                      mb: 3,
                                      borderRadius: 2,
                                      backgroundColor: 'background.default',
                                      border: isDuplicate ? '2px solid #ff9800' : '1px solid rgba(0,0,0,0.12)',
                                      boxShadow: '0 1px 3px rgba(0,0,0,0.08)'
                                    }}>
                                    {/* Duplicate Warning */}
                                    {isDuplicate && (
                                      <Box
                                        sx={{
                                          backgroundColor: '#fff3e0',
                                          borderBottom: '1px solid #ffb74d',
                                          px: 2,
                                          py: 0.75,
                                          display: 'flex',
                                          alignItems: 'center',
                                          gap: 1
                                        }}>
                                        <ErrorOutlineIcon
                                          sx={{
                                            fontSize: 16,
                                            color: '#f57c00'
                                          }}
                                        />
                                        <Typography variant="caption" fontWeight={600}>
                                          DUPLICATE: Appears {duplicateCount} times
                                        </Typography>
                                      </Box>
                                    )}

                                    {/* Manifest Header */}
                                    <Box
                                      sx={{
                                        px: 3,
                                        py: 2,
                                        borderBottom: '1px solid rgba(0,0,0,0.08)'
                                      }}>
                                      <Stack direction="row" justifyContent="space-between" alignItems="flex-start">
                                        <Box>
                                          <Typography
                                            variant="body1"
                                            sx={{
                                              fontWeight: 700,

                                              fontSize: '1rem',
                                              letterSpacing: '0.4px'
                                            }}>
                                            {manifest.id}
                                          </Typography>
                                          <Typography
                                            variant="caption"
                                            sx={{
                                              color: 'text.secondary',
                                              fontSize: '0.75rem'
                                            }}>
                                            Trailer: {manifest.trailerId || 'N/A'}
                                          </Typography>
                                        </Box>
                                        <Chip
                                          label={tripDisplayStatus}
                                          size="small"
                                          sx={{
                                            height: 22,
                                            fontSize: '0.7rem',
                                            fontWeight: 600,
                                            textTransform: 'capitalize',
                                            backgroundColor:
                                              tripDisplayStatus === TripLegStatus.completed
                                                ? 'rgba(76, 175, 80, 0.12)'
                                                : 'rgba(255, 152, 0, 0.12)',
                                            color: tripDisplayStatus === TripLegStatus.completed ? '#2e7d32' : '#ed6c02'
                                          }}
                                        />
                                      </Stack>
                                    </Box>

                                    {/* Manifest Content (unchanged) */}
                                    <Box sx={{ p: 3, backgroundColor: '#fafafa' }}>
                                      {/* SSCCs & ASNs (paired per SSCC) */}
                                      <Box>
                                        <Stack direction="row" spacing={1} alignItems="center" sx={{ mb: 1.5 }}>
                                          <InventoryIcon
                                            sx={{
                                              fontSize: 16,
                                              color: 'text.secondary'
                                            }}
                                          />
                                          <Typography variant="body2" fontWeight={600} color="text.primary">
                                            SSCCs & ASNs
                                          </Typography>
                                          <Chip
                                            label={transformedManifest.ssccsWithAsns.length}
                                            size="small"
                                            sx={{
                                              height: 18,
                                              fontSize: '0.7rem',
                                              backgroundColor: 'rgba(0,0,0,0.06)',
                                              color: 'text.secondary',
                                              fontWeight: 600
                                            }}
                                          />
                                        </Stack>

                                        <Stack spacing={1}>
                                          {transformedManifest.ssccsWithAsns.map((sscc) => (
                                            <Box
                                              key={sscc.ssccId}
                                              sx={{
                                                display: 'flex',
                                                alignItems: 'center',
                                                gap: 2,
                                                p: 1.5,
                                                backgroundColor: 'background.default',
                                                borderRadius: 1,
                                                border: '1px solid rgba(0,0,0,0.08)'
                                              }}>
                                              <Box
                                                sx={{
                                                  flex: '1 1 auto',
                                                  display: 'grid',
                                                  gridTemplateColumns: '1fr 1fr',
                                                  gap: 2
                                                }}>
                                                {/* SSCC */}
                                                <Stack direction="column">
                                                  <Typography
                                                    variant="caption"
                                                    sx={{
                                                      fontWeight: 600,
                                                      color: 'text.secondary',
                                                      my: 1
                                                    }}>
                                                    SSCC
                                                  </Typography>
                                                  <Stack direction="row">
                                                    <Typography
                                                      variant="body2"
                                                      sx={{
                                                        fontSize: '0.85rem',
                                                        fontWeight: '600'
                                                      }}>
                                                      {sscc.ssccId}
                                                    </Typography>

                                                    <Typography
                                                      variant="body2"
                                                      sx={{
                                                        ml: 2,
                                                        fontSize: '0.85rem'
                                                      }}>
                                                      {sscc.sscc?.status || 'Unknown Status'}
                                                    </Typography>

                                                    {sscc.sscc?.status ? (
                                                      <Typography
                                                        variant="body2"
                                                        sx={{
                                                          ml: 2,
                                                          fontSize: '0.85rem'
                                                        }}>
                                                        {getTimeBasedOnStatus({
                                                          status: sscc.sscc?.status as SsccStatus,
                                                          sscc: sscc.sscc
                                                        })}
                                                      </Typography>
                                                    ) : null}
                                                  </Stack>
                                                </Stack>

                                                {/* ASNs for this SSCC */}
                                                <Stack direction="column">
                                                  <Typography
                                                    variant="caption"
                                                    sx={{
                                                      fontWeight: 600,
                                                      color: 'text.secondary',
                                                      my: 1
                                                    }}>
                                                    ASNs
                                                  </Typography>

                                                  {sscc.asns.length === 0 ? (
                                                    <Typography
                                                      variant="body2"
                                                      color="text.secondary"
                                                      fontStyle="italic">
                                                      No ASNs
                                                    </Typography>
                                                  ) : (
                                                    <Stack spacing={0.25}>
                                                      {sscc.asns.map((asn) => (
                                                        <Stack direction="row">
                                                          <Typography
                                                            key={asn.asn}
                                                            variant="body2"
                                                            sx={{
                                                              fontSize: '0.85rem',
                                                              fontWeight: '600'
                                                            }}>
                                                            {asn.asn}
                                                          </Typography>

                                                          <Typography
                                                            variant="body2"
                                                            sx={{
                                                              ml: 2,
                                                              fontSize: '0.85rem'
                                                            }}>
                                                            {asn.status || 'Unknown Status'}
                                                          </Typography>

                                                          {asn.status ? (
                                                            <Typography
                                                              variant="body2"
                                                              sx={{
                                                                ml: 2,
                                                                fontSize: '0.85rem'
                                                              }}>
                                                              {`${asn.statusDate?.format(dateFormat)} ${asn.statusDate?.format(timeFormat)}`}
                                                            </Typography>
                                                          ) : null}
                                                        </Stack>
                                                      ))}
                                                    </Stack>
                                                  )}
                                                </Stack>
                                              </Box>
                                              <Tooltip
                                                title={
                                                  ![TripLegStatus.arrived, TripLegStatus.completed].includes(
                                                    tripDisplayStatus
                                                  )
                                                    ? `Trip status is ${tripDisplayStatus}, please delete SSCC in Bustle.`
                                                    : ''
                                                }
                                                arrow>
                                                <span>
                                                  <Button
                                                    variant="outlined"
                                                    size="small"
                                                    disabled={
                                                      ![TripLegStatus.arrived, TripLegStatus.completed].includes(
                                                        tripDisplayStatus
                                                      )
                                                    }
                                                    startIcon={
                                                      <DeleteIcon
                                                        sx={{
                                                          fontSize: 16
                                                        }}
                                                      />
                                                    }
                                                    onClick={() => {
                                                      const ssccStatus =
                                                        mapTripLegStatusToSsccStatus(tripDisplayStatus);

                                                      const asnStatus = mapSsccStatusToAsnStatus({
                                                        ssccStatus,
                                                        manifestStatus: manifest.status
                                                      });

                                                      setSelectedManifest({
                                                        manifest: transformedManifest,
                                                        tripDisplayStatus,
                                                        ssccStatus,
                                                        asnStatus,
                                                        tripId: trip.id,
                                                        legId: leg.id,
                                                        ssccId: sscc.ssccId
                                                      });
                                                      setOpenDeleteSSCCDialog(true);
                                                    }}
                                                    sx={{
                                                      px: 2,
                                                      py: 0.75,
                                                      fontWeight: 500,
                                                      fontSize: '0.8rem',
                                                      textTransform: 'none',
                                                      borderColor: 'rgba(211, 47, 47, 0.5)',
                                                      color: '#d32f2f',
                                                      borderRadius: 1,
                                                      '&:hover': {
                                                        borderColor: '#d32f2f',
                                                        backgroundColor: 'rgba(211, 47, 47, 0.04)'
                                                      }
                                                    }}>
                                                    Delete
                                                  </Button>
                                                </span>
                                              </Tooltip>
                                            </Box>
                                          ))}
                                        </Stack>
                                      </Box>
                                      <Divider
                                        sx={{
                                          borderColor: 'rgba(0,0,0,0.08)',
                                          my: 2
                                        }}
                                      />

                                      <Stack direction="row" spacing={1.5} justifyContent="flex-end">
                                        <Tooltip
                                          title={
                                            ![TripLegStatus.arrived, TripLegStatus.completed].includes(
                                              tripDisplayStatus
                                            )
                                              ? `Trip status is ${tripDisplayStatus}, please add SSCC in Bustle.`
                                              : ''
                                          }
                                          arrow>
                                          <span>
                                            <Button
                                              variant="outlined"
                                              size="small"
                                              disabled={
                                                ![TripLegStatus.arrived, TripLegStatus.completed].includes(
                                                  tripDisplayStatus
                                                )
                                              }
                                              startIcon={
                                                <AddIcon
                                                  sx={{
                                                    fontSize: 16
                                                  }}
                                                />
                                              }
                                              onClick={() => {
                                                const ssccStatus = mapTripLegStatusToSsccStatus(tripDisplayStatus);

                                                const asnStatus = mapSsccStatusToAsnStatus({
                                                  ssccStatus,
                                                  manifestStatus: manifest.status
                                                });

                                                handleOpenAddSSCCDialog({
                                                  manifest: transformedManifest,
                                                  tripDisplayStatus,
                                                  ssccStatus,
                                                  asnStatus,
                                                  tripId: trip.id,
                                                  legId: leg.id
                                                });
                                              }}
                                              sx={{
                                                px: 2,
                                                py: 0.75,
                                                fontWeight: 500,
                                                fontSize: '0.8rem',
                                                textTransform: 'none',
                                                borderColor: 'rgba(25, 118, 210, 0.5)',
                                                color: '#1976d2',
                                                borderRadius: 1,
                                                '&:hover': {
                                                  borderColor: '#1976d2',
                                                  backgroundColor: 'rgba(25, 118, 210, 0.04)'
                                                }
                                              }}>
                                              Add SSCC
                                            </Button>
                                          </span>
                                        </Tooltip>
                                        <Tooltip
                                          title={
                                            ![TripLegStatus.arrived, TripLegStatus.completed].includes(
                                              tripDisplayStatus
                                            )
                                              ? `Trip status is ${tripDisplayStatus}, you can not mark completed.`
                                              : ''
                                          }
                                          arrow>
                                          <span>
                                            <Button
                                              variant="contained"
                                              size="small"
                                              startIcon={
                                                <CheckCircleIcon
                                                  sx={{
                                                    fontSize: 16
                                                  }}
                                                />
                                              }
                                              onClick={() => {
                                                const ssccStatus = mapTripLegStatusToSsccStatus(tripDisplayStatus);

                                                const asnStatus = mapSsccStatusToAsnStatus({
                                                  ssccStatus,
                                                  manifestStatus: manifest.status
                                                });

                                                setSelectedManifest({
                                                  manifest: transformedManifest,
                                                  tripDisplayStatus,
                                                  ssccStatus,
                                                  asnStatus,
                                                  tripId: trip.id,
                                                  legId: leg.id
                                                });
                                                setOpenCompleteDialog(true);
                                              }}
                                              disabled={
                                                ![TripLegStatus.arrived, TripLegStatus.completed].includes(
                                                  tripDisplayStatus
                                                )
                                              }
                                              sx={{
                                                px: 2,
                                                py: 0.75,
                                                fontWeight: 500,
                                                fontSize: '0.8rem',
                                                textTransform: 'none',
                                                backgroundColor: '#4caf50',
                                                '&:hover': {
                                                  backgroundColor: '#388e3c'
                                                },
                                                '&:disabled': {
                                                  backgroundColor: 'rgba(0, 0, 0, 0.12)'
                                                },
                                                boxShadow: 'none',
                                                borderRadius: 1
                                              }}>
                                              Mark Completed
                                            </Button>
                                          </span>
                                        </Tooltip>
                                        <Tooltip
                                          title={
                                            ![TripLegStatus.arrived, TripLegStatus.completed].includes(
                                              tripDisplayStatus
                                            )
                                              ? `Trip status is ${tripDisplayStatus}, please delete manifest in Bustle.`
                                              : ''
                                          }
                                          arrow>
                                          <span>
                                            <Button
                                              variant="outlined"
                                              size="small"
                                              disabled={
                                                ![TripLegStatus.arrived, TripLegStatus.completed].includes(
                                                  tripDisplayStatus
                                                )
                                              }
                                              startIcon={
                                                <DeleteIcon
                                                  sx={{
                                                    fontSize: 16
                                                  }}
                                                />
                                              }
                                              onClick={() => {
                                                const ssccStatus = mapTripLegStatusToSsccStatus(tripDisplayStatus);

                                                const asnStatus = mapSsccStatusToAsnStatus({
                                                  ssccStatus,
                                                  manifestStatus: manifest.status
                                                });

                                                setSelectedManifest({
                                                  manifest: transformedManifest,
                                                  tripDisplayStatus,
                                                  ssccStatus,
                                                  asnStatus,
                                                  tripId: trip.id,
                                                  legId: leg.id
                                                });
                                                setOpenDeleteDialog(true);
                                              }}
                                              sx={{
                                                px: 2,
                                                py: 0.75,
                                                fontWeight: 500,
                                                fontSize: '0.8rem',
                                                textTransform: 'none',
                                                borderColor: 'rgba(211, 47, 47, 0.5)',
                                                color: '#d32f2f',
                                                borderRadius: 1,
                                                '&:hover': {
                                                  borderColor: '#d32f2f',
                                                  backgroundColor: 'rgba(211, 47, 47, 0.04)'
                                                }
                                              }}>
                                              Delete
                                            </Button>
                                          </span>
                                        </Tooltip>
                                      </Stack>
                                    </Box>
                                  </Box>
                                </Box>
                              );
                            })
                          )}
                        </Box>
                      </Box>
                    ))}
                  </Box>
                )}
              </AccordionDetails>
            </Accordion>
          ))}

          {/* Pagination */}

          <Box sx={{ height: '3rem' }} />

          <Box
            sx={{
              position: 'fixed',
              bottom: 0,
              left: 0,
              right: 0,
              zIndex: 10,
              backgroundColor: 'background.default',
              borderTop: '1px solid rgba(0,0,0,0.08)',
              py: 1.5
            }}>
            <Stack direction="row" justifyContent="center">
              <Pagination count={totalPages} page={currentPage} onChange={handlePageChange} color="primary" />
            </Stack>
          </Box>
        </Stack>
      )}

      {/* No Results */}
      {filteredTripSheets.length === 0 && (tripSearch || manifestSearch || asnSearch || selectedDate) && (
        <Box sx={{ textAlign: 'center', py: 8 }}>
          <LocalShippingIcon sx={{ fontSize: 64, color: 'text.disabled', mb: 2 }} />
          <Typography variant="h6" color="text.secondary">
            No trip sheets found
          </Typography>
        </Box>
      )}

      {/* Loading State */}
      {tripSheets.length === 0 && !tripSearch && (
        <Box sx={{ textAlign: 'center', py: 8 }}>
          <CircularProgress sx={{ mb: 2 }} />
          <Typography variant="h6" color="text.secondary">
            Loading trip sheets...
          </Typography>
          <Typography variant="body2" color="text.secondary">
            Syncing data from DataStore
          </Typography>
        </Box>
      )}

      {/* Mark as Completed Dialog */}
      <Dialog
        open={openCompleteDialog}
        onClose={() => {
          setOpenCompleteDialog(false);
          setActionReason('');
        }}
        maxWidth="sm"
        fullWidth
        PaperProps={{
          sx: {
            borderRadius: 2,
            boxShadow: '0 8px 32px rgba(0,0,0,0.12)'
          }
        }}>
        <DialogTitle
          sx={{
            borderBottom: '1px solid rgba(0,0,0,0.08)',
            px: 3,
            py: 2.5,
            backgroundColor: '#f9fafb'
          }}>
          <Stack direction="row" spacing={1.5} alignItems="center">
            <Box
              sx={{
                p: 1,
                borderRadius: 1.5,
                backgroundColor: 'background.default',
                display: 'flex',
                boxShadow: '0 1px 3px rgba(0,0,0,0.08)'
              }}>
              <CheckCircleIcon sx={{ fontSize: 22, color: '#4caf50' }} />
            </Box>
            <Box>
              <Typography variant="h6" fontWeight={600}>
                Mark Manifest as Completed
              </Typography>
              <Typography variant="caption" color="text.secondary">
                Manually complete this manifest
              </Typography>
            </Box>
          </Stack>
        </DialogTitle>
        <DialogContent sx={{ px: 3, py: 3, mt: 3 }}>
          <Stack spacing={3}>
            <Alert
              severity="info"
              icon={<ErrorOutlineIcon />}
              sx={{
                borderRadius: 1.5,
                border: '1px solid rgba(2, 136, 209, 0.2)'
              }}>
              Use this action when process or system issues prevent standard completion of the manifest. Only 'Not
              Received' ASNs will be filled in maximum quantity and receiped.
            </Alert>

            <Box
              sx={{
                p: 2.5,
                borderRadius: 1.5,
                backgroundColor: '#fafafa',
                border: '1px solid rgba(0,0,0,0.08)'
              }}>
              <Stack spacing={2}>
                <Box>
                  <Typography
                    variant="caption"
                    sx={{
                      textTransform: 'uppercase',
                      letterSpacing: 0.5,
                      fontSize: '0.7rem',
                      fontWeight: 600,
                      color: 'text.secondary'
                    }}>
                    Manifest ID
                  </Typography>
                  <Typography variant="body1" fontWeight={600} sx={{ mt: 0.5 }}>
                    {selectedManifest?.manifest?.id}
                  </Typography>
                </Box>
                <Box>
                  <Typography
                    variant="caption"
                    sx={{
                      textTransform: 'uppercase',
                      letterSpacing: 0.5,
                      fontSize: '0.7rem',
                      fontWeight: 600,
                      color: 'text.secondary'
                    }}>
                    Current Status
                  </Typography>
                  <Box sx={{ mt: 0.5 }}>
                    <Chip label={selectedManifest?.tripDisplayStatus} size="small" />
                  </Box>
                </Box>
              </Stack>
            </Box>

            <Box>
              <Typography
                variant="caption"
                sx={{
                  textTransform: 'uppercase',
                  letterSpacing: 0.5,
                  fontSize: '0.7rem',
                  fontWeight: 600,
                  color: 'text.secondary',
                  mb: 1.5,
                  display: 'block'
                }}>
                Reason for Completion *
              </Typography>
              <TextField
                fullWidth
                multiline
                rows={4}
                required
                value={actionReason}
                onChange={(e) => setActionReason(e.target.value)}
                placeholder="Explain why this manifest needs to be manually marked as completed (e.g., system error, process issue, etc.)..."
                helperText="This reason will be logged in the audit trail"
                sx={{
                  '& .MuiOutlinedInput-root': {
                    borderRadius: 1.5,
                    fontSize: '0.85rem'
                  }
                }}
              />
            </Box>
          </Stack>
        </DialogContent>
        <DialogActions
          sx={{
            px: 3,
            py: 2.5,
            backgroundColor: '#fafafa',
            borderTop: '1px solid rgba(0,0,0,0.08)'
          }}>
          <Button
            onClick={() => {
              setOpenCompleteDialog(false);
              setActionReason('');
            }}
            sx={{
              textTransform: 'none',
              fontWeight: 500,
              color: 'text.secondary',
              fontSize: '0.9rem'
            }}>
            Cancel
          </Button>
          <Button
            variant="contained"
            size="small"
            onClick={handleMarkAsCompleted}
            disabled={!actionReason.trim() || isProcessing}
            startIcon={
              isProcessing ? <CircularProgress size={16} color="inherit" /> : <CheckCircleIcon sx={{ fontSize: 16 }} />
            }
            sx={{
              px: 2,
              py: 0.75,
              fontWeight: 500,
              fontSize: '0.8rem',
              textTransform: 'none',
              backgroundColor: '#4caf50',
              '&:hover': { backgroundColor: '#388e3c' },
              boxShadow: 'none',
              borderRadius: 1
            }}>
            {isProcessing ? 'Processing...' : 'Mark Completed'}
          </Button>
        </DialogActions>
      </Dialog>

      {/* Add SSCC to Manifest Dialog */}
      {selectedManifest ? (
        <Dialog
          open={openAddSSCCDialog}
          onClose={() => {
            setOpenAddSSCCDialog(false);
            setSelectedSSCCId('');
            setAddSSCCReason('');
            setSsccWeight('0');
            setWithReceiveManifest(true);
          }}
          maxWidth="sm"
          fullWidth
          PaperProps={{
            sx: {
              borderRadius: 2,
              boxShadow: '0 8px 32px rgba(0,0,0,0.12)'
            }
          }}>
          <DialogTitle
            sx={{
              borderBottom: '1px solid rgba(0,0,0,0.08)',
              px: 3,
              py: 2.5
            }}>
            <Stack direction="row" spacing={1.5} alignItems="center">
              <Box
                sx={{
                  p: 1,
                  borderRadius: 1.5,
                  backgroundColor: 'background.default',
                  display: 'flex',
                  boxShadow: '0 1px 3px rgba(0,0,0,0.08)'
                }}>
                <AddIcon sx={{ fontSize: 22, color: '#1976d2' }} />
              </Box>
              <Box>
                <Typography variant="h6" fontWeight={600}>
                  Add SSCC to Manifest
                </Typography>
                <Typography variant="caption" color="text.secondary">
                  Add an SSCC that was received without a manifest
                </Typography>
              </Box>
            </Stack>
          </DialogTitle>
          <DialogContent sx={{ px: 3, py: 3, mt: 3 }}>
            <Stack spacing={3}>
              <Alert
                severity="info"
                icon={<InfoOutlinedIcon />}
                sx={{
                  borderRadius: 1.5,
                  border: '1px solid rgba(2, 136, 209, 0.2)',
                  fontSize: '0.85rem'
                }}>
                Adding this SSCC will update its status to{' '}
                <Box component="span" sx={{ fontWeight: 600 }}>
                  {`'${selectedManifest.ssccStatus}'`}
                </Box>{' '}
                and all ASN statuses to{' '}
                <Box component="span" sx={{ fontWeight: 600 }}>
                  {`'${selectedManifest.asnStatus}'`}
                </Box>
                {'. '}
                {selectedManifest?.manifest?.status === 'Completed' && (
                  <>You can choose whether or not to receive the manifest.</>
                )}
              </Alert>

              <Box
                sx={{
                  p: 2.5,
                  borderRadius: 1.5,
                  backgroundColor: '#fafafa',
                  border: '1px solid rgba(0,0,0,0.08)'
                }}>
                <Stack spacing={2}>
                  <Typography
                    variant="caption"
                    sx={{
                      textTransform: 'uppercase',
                      letterSpacing: 0.5,
                      fontSize: '0.7rem',
                      fontWeight: 600,
                      color: 'text.secondary'
                    }}>
                    Manifest ID
                  </Typography>
                  <Typography variant="body1" fontWeight={600} sx={{ mt: 0.5 }}>
                    {selectedManifest?.manifest?.id}
                  </Typography>
                </Stack>
              </Box>

              <Box>
                <Typography
                  variant="caption"
                  sx={{
                    textTransform: 'uppercase',
                    letterSpacing: 0.5,
                    fontSize: '0.7rem',
                    fontWeight: 600,
                    color: 'text.secondary',
                    mb: 1.5,
                    display: 'block'
                  }}>
                  Select SSCC *
                </Typography>
                <Autocomplete
                  options={availableSSCCs}
                  getOptionLabel={(option) => `${option.id} - ${option.status || 'Unknown'}`}
                  value={availableSSCCs.find((s) => s.id === selectedSSCCId) || null}
                  onChange={(_, newValue) => setSelectedSSCCId(newValue?.id || '')}
                  renderInput={(params) => (
                    <TextField
                      {...params}
                      placeholder="Search for SSCC..."
                      sx={{
                        '& .MuiOutlinedInput-root': {
                          borderRadius: 1.5,
                          fontSize: '0.85rem'
                        }
                      }}
                    />
                  )}
                  renderOption={(props, option) => (
                    <li {...props}>
                      <Stack direction="row" spacing={1.5} alignItems="center" sx={{ width: '100%', py: 1 }}>
                        <Box
                          sx={{
                            p: 1,
                            borderRadius: 1,
                            backgroundColor: 'rgba(25, 118, 210, 0.08)',
                            display: 'flex'
                          }}>
                          <InventoryIcon sx={{ fontSize: 18, color: 'primary.main' }} />
                        </Box>
                        <Box sx={{ flexGrow: 1 }}>
                          <Typography variant="body2" fontWeight={600}>
                            {option.id}
                          </Typography>
                          <Typography variant="caption" color="text.secondary">
                            {option.destinationLocation?.name || 'Unknown destination'} • {option.status || 'Unknown'}
                          </Typography>
                        </Box>
                      </Stack>
                    </li>
                  )}
                />
              </Box>

              <Box>
                <Typography
                  variant="caption"
                  sx={{
                    textTransform: 'uppercase',
                    letterSpacing: 0.5,
                    fontSize: '0.7rem',
                    fontWeight: 600,
                    color: 'text.secondary',
                    mb: 1.5,
                    display: 'block'
                  }}>
                  Reason for Adding *
                </Typography>
                <TextField
                  fullWidth
                  multiline
                  rows={4}
                  required
                  value={addSSCCReason}
                  onChange={(e) => setAddSSCCReason(e.target.value)}
                  placeholder="Explain why this SSCC is being added to the manifest (e.g., received without manifest, system error, etc.)..."
                  helperText="This reason will be logged in the audit trail"
                  sx={{
                    '& .MuiOutlinedInput-root': {
                      borderRadius: 1.5,
                      fontSize: '0.95rem'
                    }
                  }}
                />
              </Box>

              {selectedSSCCId && !selectedSSCC?.weight ? (
                <Box display="flex" flexDirection="column" gap={0.25}>
                  <Typography variant="body2" sx={{ fontSize: '0.8rem' }}>
                    Weight
                  </Typography>

                  <TextField
                    size="small"
                    placeholder="Enter Weight"
                    type="number"
                    value={ssccWeight}
                    onChange={(e) => {
                      const value = Number(e.target.value);

                      if (value <= 500000) {
                        setSsccWeight(value.toString());
                      } else {
                        alert('Weight (kg) cannot exceed 500,000', 'error');
                      }
                    }}
                    InputProps={{
                      endAdornment: (
                        <InputAdornment position="end" sx={{ fontSize: '0.55rem' }}>
                          KG
                        </InputAdornment>
                      ),
                      sx: {
                        fontSize: '0.85rem',
                        paddingY: 0.5
                      }
                    }}
                    inputProps={{
                      style: {
                        fontSize: '0.85rem'
                      }
                    }}
                    sx={{
                      maxWidth: 200
                    }}
                  />
                </Box>
              ) : null}
              {selectedManifest?.manifest?.status === 'Completed' ? (
                <Box display="flex" alignItems="center">
                  <Checkbox
                    size="small"
                    checked={withReceiveManifest}
                    onChange={(e) => setWithReceiveManifest(e.target.checked)}
                  />
                  <Typography variant="body2" sx={{ fontSize: '0.9rem' }}>
                    Receive Manifest
                  </Typography>
                </Box>
              ) : null}
            </Stack>
          </DialogContent>
          <DialogActions
            sx={{
              px: 3,
              py: 2.5,
              backgroundColor: '#fafafa',
              borderTop: '1px solid rgba(0,0,0,0.08)'
            }}>
            <Button
              onClick={() => {
                setOpenAddSSCCDialog(false);
                setSelectedSSCCId('');
                setAddSSCCReason('');
                setSsccWeight('0');
                setWithReceiveManifest(true);
              }}
              sx={{
                textTransform: 'none',
                fontWeight: 500,
                color: 'text.secondary',
                fontSize: '0.9rem'
              }}>
              Cancel
            </Button>
            <Button
              variant="contained"
              size="small"
              onClick={() =>
                handleAddSSCCToManifest({
                  withReceiveManifestCheck: selectedManifest?.manifest?.status === 'Completed'
                })
              }
              disabled={!selectedSSCCId || !addSSCCReason.trim() || isProcessing}
              startIcon={
                isProcessing ? <CircularProgress size={16} color="inherit" /> : <AddIcon sx={{ fontSize: 16 }} />
              }
              sx={{
                px: 2,
                py: 0.75,
                fontWeight: 500,
                fontSize: '0.8rem',
                textTransform: 'none',
                backgroundColor: '#1976d2',
                '&:hover': { backgroundColor: '#1565c0' },
                boxShadow: 'none',
                borderRadius: 1
              }}>
              {isProcessing ? 'Adding...' : 'Add SSCC'}
            </Button>
          </DialogActions>
        </Dialog>
      ) : null}

      {/* Bustle Confirmation Dialog */}
      <Dialog
        open={openBustleDialog}
        onClose={() => setOpenBustleDialog(false)}
        maxWidth="xs"
        fullWidth
        PaperProps={{
          sx: {
            borderRadius: 2,
            boxShadow: '0 8px 32px rgba(0,0,0,0.12)'
          }
        }}>
        <DialogTitle
          sx={{
            borderBottom: '1px solid rgba(0,0,0,0.08)',
            px: 3,
            py: 2.5,
            backgroundColor: '#fff3e0'
          }}>
          <Stack direction="row" spacing={1.5} alignItems="center">
            <Box
              sx={{
                p: 1,
                borderRadius: 1.5,
                backgroundColor: 'background.default',
                display: 'flex',
                boxShadow: '0 1px 3px rgba(0,0,0,0.08)'
              }}>
              <ErrorOutlineIcon sx={{ fontSize: 22, color: '#ff9800' }} />
            </Box>
            <Box>
              <Typography variant="h6" fontWeight={600}>
                Action Required
              </Typography>
            </Box>
          </Stack>
        </DialogTitle>
        <DialogContent sx={{ px: 3, py: 3, mt: 3 }}>
          <Alert severity="warning" sx={{ borderRadius: 1.5 }}>
            <Typography variant="body2" fontWeight={600} sx={{ mb: 1 }}>
              Access Bustle and Cancel Job
            </Typography>
            <Typography variant="body2">
              Please access Bustle to cancel any related jobs for this SSCC addition.
            </Typography>
          </Alert>
        </DialogContent>
        <DialogActions
          sx={{
            px: 3,
            py: 2.5,
            backgroundColor: '#fafafa',
            borderTop: '1px solid rgba(0,0,0,0.08)'
          }}>
          <Button
            variant="contained"
            onClick={() => setOpenBustleDialog(false)}
            sx={{
              textTransform: 'none',
              fontWeight: 500,
              backgroundColor: '#ff9800',
              '&:hover': { backgroundColor: '#f57c00' }
            }}>
            Understood
          </Button>
        </DialogActions>
      </Dialog>
      <Dialog
        open={openCoupaDialog}
        onClose={() => setOpenCoupaDialog(false)}
        maxWidth="xs"
        fullWidth
        PaperProps={{
          sx: {
            borderRadius: 2,
            boxShadow: '0 8px 32px rgba(0,0,0,0.12)'
          }
        }}>
        <DialogTitle
          sx={{
            borderBottom: '1px solid rgba(0,0,0,0.08)',
            px: 3,
            py: 2.5,
            backgroundColor: '#fff3e0'
          }}>
          <Stack direction="row" spacing={1.5} alignItems="center">
            <Box
              sx={{
                p: 1,
                borderRadius: 1.5,
                backgroundColor: 'background.default',
                display: 'flex',
                boxShadow: '0 1px 3px rgba(0,0,0,0.08)'
              }}>
              <ErrorOutlineIcon sx={{ fontSize: 22, color: '#ff9800' }} />
            </Box>
            <Box>
              <Typography variant="h6" fontWeight={600}>
                Action Required
              </Typography>
            </Box>
          </Stack>
        </DialogTitle>
        <DialogContent sx={{ px: 3, py: 3, mt: 3 }}>
          <Alert severity="warning" sx={{ borderRadius: 1.5 }}>
            <Typography variant="body2" fontWeight={600} sx={{ mb: 1 }}>
              Access Coupa and check Inventory Transfer
            </Typography>
            <Typography variant="body2">
              Please access Coupa to check already transferred inventory related to this deletion.
            </Typography>
          </Alert>
        </DialogContent>
        <DialogActions
          sx={{
            px: 3,
            py: 2.5,
            backgroundColor: '#fafafa',
            borderTop: '1px solid rgba(0,0,0,0.08)'
          }}>
          <Button
            variant="contained"
            onClick={() => setOpenCoupaDialog(false)}
            sx={{
              textTransform: 'none',
              fontWeight: 500,
              backgroundColor: '#ff9800',
              '&:hover': { backgroundColor: '#f57c00' }
            }}>
            Understood
          </Button>
        </DialogActions>
      </Dialog>
      {/* Delete Manifest Dialog */}
      <Dialog
        open={openDeleteDialog}
        onClose={() => {
          setOpenDeleteDialog(false);
          setActionReason('');
        }}
        maxWidth="sm"
        fullWidth
        PaperProps={{
          sx: {
            borderRadius: 2,
            boxShadow: '0 8px 32px rgba(0,0,0,0.12)'
          }
        }}>
        <DialogTitle
          sx={{
            borderBottom: '1px solid rgba(0,0,0,0.08)',
            px: 3,
            py: 2.5,
            backgroundColor: '#fef6f6'
          }}>
          <Stack direction="row" spacing={1.5} alignItems="center">
            <Box
              sx={{
                p: 1,
                borderRadius: 1.5,
                backgroundColor: 'background.default',
                display: 'flex',
                boxShadow: '0 1px 3px rgba(0,0,0,0.08)'
              }}>
              <DeleteIcon sx={{ fontSize: 22, color: '#d32f2f' }} />
            </Box>
            <Box>
              <Typography variant="h6" fontWeight={600}>
                Delete Manifest
              </Typography>
              <Typography variant="caption" color="text.secondary">
                Permanently remove this manifest
              </Typography>
            </Box>
          </Stack>
        </DialogTitle>
        <DialogContent sx={{ px: 3, py: 3, mt: 3 }}>
          <Stack spacing={3}>
            <Alert
              severity="warning"
              icon={<ErrorOutlineIcon />}
              sx={{
                borderRadius: 1.5,
                border: '1px solid rgba(237, 108, 2, 0.2)'
              }}>
              This action will permanently mark the manifest as deleted. This action cannot be undone.
            </Alert>

            <Box
              sx={{
                p: 2.5,
                borderRadius: 1.5,
                backgroundColor: '#fafafa',
                border: '1px solid rgba(0,0,0,0.08)'
              }}>
              <Stack spacing={2}>
                <Box>
                  <Typography
                    variant="caption"
                    sx={{
                      textTransform: 'uppercase',
                      letterSpacing: 0.5,
                      fontSize: '0.7rem',
                      fontWeight: 600,
                      color: 'text.secondary'
                    }}>
                    Manifest ID
                  </Typography>
                  <Typography variant="body1" fontWeight={600} sx={{ mt: 0.5 }}>
                    {selectedManifest?.manifest?.id}
                  </Typography>
                </Box>
                <Box>
                  <Typography
                    variant="caption"
                    sx={{
                      textTransform: 'uppercase',
                      letterSpacing: 0.5,
                      fontSize: '0.7rem',
                      fontWeight: 600,
                      color: 'text.secondary'
                    }}>
                    Current Status
                  </Typography>
                  <Box sx={{ mt: 0.5 }}>
                    <Chip label={selectedManifest?.tripDisplayStatus} size="small" />
                  </Box>
                </Box>
              </Stack>
            </Box>

            <Box>
              <Typography
                variant="caption"
                sx={{
                  textTransform: 'uppercase',
                  letterSpacing: 0.5,
                  fontSize: '0.7rem',
                  fontWeight: 600,
                  color: 'text.secondary',
                  mb: 1.5,
                  display: 'block'
                }}>
                Reason for Deletion *
              </Typography>
              <TextField
                fullWidth
                multiline
                rows={4}
                required
                value={actionReason}
                onChange={(e) => setActionReason(e.target.value)}
                placeholder="Explain why this manifest is being deleted (e.g., duplicate, erroneous entry, etc.)..."
                helperText="This reason will be logged in the audit trail"
                sx={{
                  '& .MuiOutlinedInput-root': {
                    borderRadius: 1.5,
                    fontSize: '0.85rem'
                  }
                }}
              />
            </Box>
          </Stack>
        </DialogContent>
        <DialogActions
          sx={{
            px: 3,
            py: 2.5,
            backgroundColor: '#fafafa',
            borderTop: '1px solid rgba(0,0,0,0.08)'
          }}>
          <Button
            onClick={() => {
              setOpenDeleteDialog(false);
              setActionReason('');
            }}
            sx={{
              textTransform: 'none',
              fontWeight: 500,
              color: 'text.secondary',
              fontSize: '0.9rem'
            }}>
            Cancel
          </Button>
          <Button
            variant="outlined"
            size="small"
            onClick={handleDeleteManifest}
            disabled={!actionReason.trim() || isProcessing}
            startIcon={
              isProcessing ? <CircularProgress size={16} color="inherit" /> : <DeleteIcon sx={{ fontSize: 16 }} />
            }
            sx={{
              px: 2,
              py: 0.75,
              fontWeight: 500,
              fontSize: '0.8rem',
              textTransform: 'none',
              borderColor: 'rgba(211, 47, 47, 0.5)',
              color: '#d32f2f',
              borderRadius: 1,
              '&:hover': {
                borderColor: '#d32f2f',
                backgroundColor: 'rgba(211, 47, 47, 0.04)'
              }
            }}>
            {isProcessing ? 'Deleting...' : 'Delete'}
          </Button>
        </DialogActions>
      </Dialog>

      {/* Delete Manifest Dialog */}
      <Dialog
        open={openDeleteSSCCDialog}
        onClose={() => {
          setOpenDeleteSSCCDialog(false);
          setActionReason('');
        }}
        maxWidth="sm"
        fullWidth
        PaperProps={{
          sx: {
            borderRadius: 2,
            boxShadow: '0 8px 32px rgba(0,0,0,0.12)'
          }
        }}>
        <DialogTitle
          sx={{
            borderBottom: '1px solid rgba(0,0,0,0.08)',
            px: 3,
            py: 2.5,
            backgroundColor: '#fef6f6'
          }}>
          <Stack direction="row" spacing={1.5} alignItems="center">
            <Box
              sx={{
                p: 1,
                borderRadius: 1.5,
                backgroundColor: 'background.default',
                display: 'flex',
                boxShadow: '0 1px 3px rgba(0,0,0,0.08)'
              }}>
              <DeleteIcon sx={{ fontSize: 22, color: '#d32f2f' }} />
            </Box>
            <Box>
              <Typography variant="h6" fontWeight={600}>
                Delete SSCC
              </Typography>
              <Typography variant="caption" color="text.secondary">
                Permanently remove this SSCC
              </Typography>
            </Box>
          </Stack>
        </DialogTitle>
        <DialogContent sx={{ px: 3, py: 3, mt: 3 }}>
          <Stack spacing={3}>
            <Alert
              severity="warning"
              icon={<ErrorOutlineIcon />}
              sx={{
                borderRadius: 1.5,
                border: '1px solid rgba(237, 108, 2, 0.2)'
              }}>
              This action will permanently mark the SSCC as deleted from the manifest. This action cannot be undone.
            </Alert>

            <Box
              sx={{
                p: 2.5,
                borderRadius: 1.5,
                backgroundColor: '#fafafa',
                border: '1px solid rgba(0,0,0,0.08)'
              }}>
              <Stack spacing={2}>
                <Box>
                  <Typography
                    variant="caption"
                    sx={{
                      textTransform: 'uppercase',
                      letterSpacing: 0.5,
                      fontSize: '0.7rem',
                      fontWeight: 600,
                      color: 'text.secondary'
                    }}>
                    SSCC
                  </Typography>
                  <Typography variant="body1" fontWeight={600} sx={{ mt: 0.5 }}>
                    {selectedManifest?.ssccId}
                  </Typography>
                </Box>
                <Box>
                  <Typography
                    variant="caption"
                    sx={{
                      textTransform: 'uppercase',
                      letterSpacing: 0.5,
                      fontSize: '0.7rem',
                      fontWeight: 600,
                      color: 'text.secondary'
                    }}>
                    Current Status
                  </Typography>
                  <Box sx={{ mt: 0.5 }}>
                    <Chip label={selectedManifest?.ssccStatus} size="small" />
                  </Box>
                </Box>
              </Stack>
            </Box>

            <Box>
              <Typography
                variant="caption"
                sx={{
                  textTransform: 'uppercase',
                  letterSpacing: 0.5,
                  fontSize: '0.7rem',
                  fontWeight: 600,
                  color: 'text.secondary',
                  mb: 1.5,
                  display: 'block'
                }}>
                Reason for Deletion *
              </Typography>
              <TextField
                fullWidth
                multiline
                rows={4}
                required
                value={actionReason}
                onChange={(e) => setActionReason(e.target.value)}
                placeholder="Explain why this SSCC is being deleted (e.g., duplicate, erroneous entry, etc.)..."
                helperText="This reason will be logged in the audit trail"
                sx={{
                  '& .MuiOutlinedInput-root': {
                    borderRadius: 1.5,
                    fontSize: '0.85rem'
                  }
                }}
              />
            </Box>
          </Stack>
        </DialogContent>
        <DialogActions
          sx={{
            px: 3,
            py: 2.5,
            backgroundColor: '#fafafa',
            borderTop: '1px solid rgba(0,0,0,0.08)'
          }}>
          <Button
            onClick={() => {
              setOpenDeleteSSCCDialog(false);
              setActionReason('');
            }}
            sx={{
              textTransform: 'none',
              fontWeight: 500,
              color: 'text.secondary',
              fontSize: '0.9rem'
            }}>
            Cancel
          </Button>
          <Button
            variant="outlined"
            size="small"
            onClick={handleDeleteSSCC}
            disabled={!actionReason.trim() || isProcessing}
            startIcon={
              isProcessing ? <CircularProgress size={16} color="inherit" /> : <DeleteIcon sx={{ fontSize: 16 }} />
            }
            sx={{
              px: 2,
              py: 0.75,
              fontWeight: 500,
              fontSize: '0.8rem',
              textTransform: 'none',
              borderColor: 'rgba(211, 47, 47, 0.5)',
              color: '#d32f2f',
              borderRadius: 1,
              '&:hover': {
                borderColor: '#d32f2f',
                backgroundColor: 'rgba(211, 47, 47, 0.04)'
              }
            }}>
            {isProcessing ? 'Deleting...' : 'Delete'}
          </Button>
        </DialogActions>
      </Dialog>
    </Stack>
  );
}
